import { DateRangePickerWrapper } from 'components/Date/DateRangePicker';
import { tableStyles } from 'css/tableStyles';
import React, { useState } from 'react';
import DataTable from 'components/DataTable';
import { DATE_FORMATS, formatDate, getEmailsArray, showError } from 'utils';
import moment from 'moment';
import styles from './index.module.scss';
import ActionButton from 'components/ActionButton';
import ExportCSVButton from 'components/ExportCSVButton';
import { fetchRefundReport } from 'services';
import Currency from 'components/Currency/Currency';

const columns = [
  {
    name: 'ID',
    selector: 'objectId',
    width: '120px',
  },
  {
    name: 'Created At',
    selector: 'createdAt',
    width: '180px',
    sortable: true,
    format: (row) => formatDate(row.createdAt, DATE_FORMATS.MM_DD_YYYY_hh_mm_A),
  },
  {
    name: 'Order ID',
    selector: 'orderId',
    width: '120px',
  },
  {
    name: 'Amount',
    selector: 'amount',
    width: '100px',
    // eslint-disable-next-line react/display-name
    format: (row) => (<Currency amount={parseFloat(row.amount?.toFixed(2))} default={'-'} />),
  },
  {
    name: 'Type',
    selector: 'type',
    width: '120px',
  },
  {
    name: 'Item Name',
    selector: 'itemName',
    width: '220px',
  },
  {
    name: 'Item Refund Reason',
    selector: 'itemRefundReason',
    width: '260px',
  },
  {
    name: 'Item Refunded Count',
    selector: 'itemRefundedCount',
    width: '80px',
  },
];

const columnsForCSV = [
  {
    label: 'ID',
    value: 'objectId',
  },
  {
    label: 'Created At',
    value: 'createdAt',
  },
  {
    label: 'Order ID',
    value: 'orderId',
  },
  {
    label: 'Amount ($)',
    value: 'amount',
  },
  {
    label: 'Type',
    value: 'type',
  },
  {
    label: 'Item Name',
    value: 'itemName',
  },
  {
    label: 'Item Refund Reason',
    value: 'itemRefundReason',
  },
  {
    label: 'Item Refunded Count',
    value: 'itemRefundedCount',
  },
];

const RefundReport = props => {
  const [data, setData] = useState<Record<string, any>[]>([]);
  const [startDate, setStartDate] = useState<string>(moment().subtract(1, 'month').toISOString());
  const [endDate, setEndDate] = useState<string>(moment().toISOString());
  const [email, setEmail] = useState<string>('');
  const [sendEmail, setSendEmail] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchRefundReportFn = async () => {
    setLoading(true);
    const data = {
      startDate,
      endDate,
      emails: getEmailsArray(email),
      sendEmail,
    };
    try {
      const response = await fetchRefundReport(data);
      setData(response);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  const onDateSelectionChange = ({ startDate, endDate }: { startDate: Date; endDate: Date }) => {
    setStartDate(moment(startDate).toISOString());
    setEndDate(moment(endDate).toISOString());
  };

  return (
    <div>
      <div className={styles['header']}>
        <div className={styles['heading']}>Refunds Report</div>
        <ExportCSVButton
          fileName={'Refunds Report'}
          data={data}
          columnsForCSV={columnsForCSV}
        />
      </div>
      <div className={styles['filter-container']}>
        <div>
          <div className={styles['label']}>Date Range</div>
          <DateRangePickerWrapper
            disabled={loading}
            startDate={startDate}
            endDate={endDate}
            onDatesChange={onDateSelectionChange}
          />
        </div>
        <div>
          <div className={styles['label']}>Emails</div>
          <input
            className={'form-control'}
            disabled={loading || !sendEmail}
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
          <div className={styles['helper']}>Use commas to separate emails</div>
        </div>
        <div className={'d-flex my-auto'}>
          <input
            type={'checkbox'}
            disabled={loading}
            checked={sendEmail}
            onChange={e => setSendEmail(e.target.checked)}
          />
          <div className={styles['']}>Send Email</div>
        </div>
        <ActionButton
          text={'Submit'}
          active={!loading}
          isLoading={false}
          onClick={fetchRefundReportFn}
          customClass={'ml-4 my-auto'}
        />
      </div>
      <DataTable
        noHeader
        data={data}
        columns={columns}
        customStyles={tableStyles}
      />
    </div>
  );
};

export default RefundReport;